import { useCallback, useState } from 'react';
import { useShouldAutoInsure } from '../../services/insurance-service';
import type { InsuranceProvider } from '../../enums/insurance-provider';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { updateSellerSettingsBulk } from '../../pages/settings/api/save-settings';

export type UseUpdateInsurancePreference = {
  shouldUpdateInsurancePreference: boolean;
  setShouldUpdateInsurancePreference: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateInsurancePreference: (insuranceProvider: InsuranceProvider) => void;
  isInsuranceOptionDirty: boolean;
  setIsInsuranceOptionDirty: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useUpdateInsurancePreference = (insuranceProvider: InsuranceProvider): UseUpdateInsurancePreference => {
  const [isInsuranceOptionDirty, setIsInsuranceOptionDirty] = useState(false);
  const [shouldUpdatePreference, setShouldUpdatePreference] = useState(false);
  const { setShouldAutoInsure } = useShouldAutoInsure(insuranceProvider?.insuranceProviderId);

  const handleUpdateInsurancePreference = useCallback(() => {
    if (!shouldUpdatePreference) {
      return;
    }

    if (
      insuranceProvider?.insuranceProviderId === InsuranceProviders.ParcelGuard.insuranceProviderId ||
      insuranceProvider?.insuranceProviderId === InsuranceProviders.XCover.insuranceProviderId
    ) {
      setShouldAutoInsure('true');
      return;
    }

    if (insuranceProvider?.insuranceProviderId === InsuranceProviders.None.insuranceProviderId) {
      updateSellerSettingsBulk({
        [SellerSettings.insuranceSystemAutomationPreferenceNone.key]: 'true',
      });
    }
  }, [shouldUpdatePreference, setShouldAutoInsure, insuranceProvider?.insuranceProviderId]);

  return {
    shouldUpdateInsurancePreference: shouldUpdatePreference,
    setShouldUpdateInsurancePreference: setShouldUpdatePreference,
    handleUpdateInsurancePreference,
    isInsuranceOptionDirty,
    setIsInsuranceOptionDirty,
  };
};
